import { match } from 'ts-pattern';
import api from './api';
import {
  ClaimType,
  ClaimTypeProgramName,
  ClaimTypeProgramPluralName,
} from './constants';
import { AsyncReturnType } from './types';

type Theme = AsyncReturnType<typeof api.fetchSettings>['theme'];

const setFavicon = (url: string) => {
  const links = document.querySelectorAll<HTMLLinkElement>("link[rel='icon']");
  links.forEach((link) => link.setAttribute('href', url));
};

const setTitle = (title?: string) => {
  if (title) {
    document.title = title;
  }
};

const setCustomStyles = (content: string) => {
  const stylesId = 'corso-theme-styles';
  let stylesElement = document.getElementById(stylesId);

  if (!stylesElement) {
    stylesElement = document.createElement('style');
    stylesElement.setAttribute('id', stylesId);
    document.head.appendChild(stylesElement);
  }

  stylesElement.textContent = content;
};

const getCustomPropertyOverrides = (theme: Theme) => {
  const properties = [];

  if (theme.merchantPrimaryColor) {
    properties.push(
      `--color-brand: ${theme.merchantPrimaryColor}`,
      `--color-on-brand: #ffffff`,
      `--color-brand-container: ${theme.merchantPrimaryColor}05`,
      `--color-on-brand-container: ${theme.merchantPrimaryColor}`,
    );
  }

  if (theme.merchantBackgroundColor) {
    properties.push(`--color-surface: ${theme.merchantBackgroundColor}`);
  }

  if (theme.merchantBackgroundUrl) {
    properties.push(
      `--corso-banner-img-url: url('${theme.merchantBackgroundUrl}')`,
    );
  }

  return properties.join('; ');
};

export const setTheme = (theme: Theme) => {
  setCustomStyles(
    `${theme.customCss ?? ''}
    :root { ${getCustomPropertyOverrides(theme)}; }`.trim(),
  );
  setFavicon(theme.merchantFaviconUrl);
  setTitle(theme.merchantPageTitle);
};

export const isAddressPhoneRequired = (
  theme: Theme,
  claimType?: ClaimType | null,
) =>
  match(claimType)
    .with(ClaimType.warranty, () => !!theme.isWarrantyPhoneRequired)
    .with(ClaimType.return, () => !!theme.isReturnPhoneRequired)
    .otherwise(() => false);

export const getClaimTypeProgramName = (theme: Theme, claimType: ClaimType) =>
  match(claimType)
    .with(ClaimType.return, () => ClaimTypeProgramName[ClaimType.return])
    .with(
      ClaimType.warranty,
      () =>
        ClaimTypeProgramName[theme.warrantyProgramName ?? ClaimType.warranty],
    )
    .exhaustive();

export const getOriginatingOrderCreditAlert = (theme: Theme) => ({
  title: theme.originatingOrderCreditTitleText ?? 'Refund Amount Pending',
  detail:
    theme.originatingOrderCreditDetailText ??
    'The amount shown is an estimate, and will be confirmed after reviewing your return.',
});

export const getClaimTypeProgramPluralName = (
  theme: Theme,
  claimType: ClaimType,
) =>
  match(claimType)
    .with(ClaimType.return, () => ClaimTypeProgramPluralName[ClaimType.return])
    .with(
      ClaimType.warranty,
      () =>
        ClaimTypeProgramPluralName[
          theme.warrantyProgramName ?? ClaimType.warranty
        ],
    )
    .exhaustive();
